/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MONTHS_CALENDAR } from '@constants';

@Component({
  selector: 'unicon-datetime-calendar',
  templateUrl: './datetime-calendar.component.html',
  styleUrls: ['./datetime-calendar.component.scss'],
})
export class DatetimeCalendarComponent implements OnInit, OnChanges {
  @Output() selectionChangeTime = new EventEmitter<any>();
  @Input() start?: Date | string | null;
  @Input() end?: Date | string | null;
  protected range: FormGroup;
  protected activeField: 'start' | 'end' = 'start';
  protected displayedMonth: number;
  protected displayedYear: number;
  protected calendarDays: Date[] = [];
  private monthNames = MONTHS_CALENDAR;
  protected selected: Date | null = null;

  constructor(private fb: FormBuilder) {
    this.range = this.fb.group({ start: [null], end: [null] });
    const today = new Date();
    this.displayedMonth = today.getMonth();
    this.displayedYear = today.getFullYear();

    this.range.valueChanges.subscribe(val => {
      const start = val.start as Date | null;
      const end = val.end as Date | null;
      this.selected = start ?? end ?? null;
    });
  }

  protected formatMonthYear(): string {
    const name = this.monthNames[this.displayedMonth] || '';
    return `${name} ${this.displayedYear}`;
  }

  ngOnInit(): void {
    this.generateCalendar(this.displayedMonth, this.displayedYear);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['start'] || changes['end']) {
      const s = this.toDate(this.start);
      const e = this.toDate(this.end);
      this.range.patchValue(
        { start: s ?? null, end: e ?? null },
        { emitEvent: true },
      );
      this.selected = s ?? e ?? null;
    }
  }

  protected setActive(field: 'start' | 'end') {
    this.activeField = field;
  }

  protected onDateSelected(date: Date) {
    const d = this.normalizeDate(date);
    const { start } = this.range.value;

    if (this.activeField === 'start') {
      this.range.patchValue({ start: d, end: null });
      this.activeField = 'end';
    } else {
      if (start && d < this.normalizeDate(start)) {
        this.range.patchValue({ start: d, end: start });
      } else {
        this.range.patchValue({ end: d });
      }
      this.activeField = 'start';
    }
  }

  protected submit() {
    const { start, end } = this.range.value;
    if (start && end) {
      this.selectionChangeTime.emit({ fromDate: start, toDate: end });
    }
  }

  protected formatDate(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private normalizeDate(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private isSame(a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    return this.normalizeDate(a).getTime() === this.normalizeDate(b).getTime();
  }

  protected generateCalendar(month: number, year: number) {
    this.calendarDays = [];
    const first = new Date(year, month, 1);
    const startIndex = first.getDay();
    for (let i = startIndex - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      this.calendarDays.push(this.normalizeDate(d));
    }
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      this.calendarDays.push(new Date(year, month, d));
    }
    while (this.calendarDays.length % 7 !== 0) {
      const last = this.calendarDays[this.calendarDays.length - 1];
      const next = new Date(
        last.getFullYear(),
        last.getMonth(),
        last.getDate() + 1,
      );
      this.calendarDays.push(this.normalizeDate(next));
    }
  }

  protected prevMonth() {
    if (this.displayedMonth === 0) {
      this.displayedMonth = 11;
      this.displayedYear -= 1;
    } else {
      this.displayedMonth -= 1;
    }
    this.generateCalendar(this.displayedMonth, this.displayedYear);
  }

  protected nextMonth() {
    if (this.displayedMonth === 11) {
      this.displayedMonth = 0;
      this.displayedYear += 1;
    } else {
      this.displayedMonth += 1;
    }
    this.generateCalendar(this.displayedMonth, this.displayedYear);
  }

  protected isInRange(day: Date): boolean {
    const { start, end } = this.range.value;
    if (!start || !end) return false;
    const n = this.normalizeDate(day).getTime();
    const s = this.normalizeDate(start).getTime();
    const e = this.normalizeDate(end).getTime();
    return n >= Math.min(s, e) && n <= Math.max(s, e);
  }

  protected isStart(day: Date) {
    return this.isSame(this.range.value.start, day);
  }

  protected isEnd(day: Date) {
    return this.isSame(this.range.value.end, day);
  }

  private toDate(value: Date | string | null | undefined): Date | null {
    if (value == null) return null;
    if (value instanceof Date) return this.normalizeDate(value);

    const s = String(value).trim();

    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const y = Number(ymd[1]);
      const m = Number(ymd[2]) - 1; // monthIndex
      const d = Number(ymd[3]);
      return this.normalizeDate(new Date(y, m, d));
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : this.normalizeDate(parsed);
  }
}
